import { memo, useContext, useRef, useState } from 'react';
import { useFieldArray } from 'react-hook-form';

import { useLanguage } from '@languages/index';
import CampaignFormField from '../../../../../../../campaignFormFieldGroup/components/campaignFormField/campaignFormField';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import useUploadGuidelineImage from 'hooks/useUploadGuidelineImage/useUploadGuidelineImage';

const BrandLogoField = () => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };

  const { guidelineRuleFormMethods, modal } = useContext(CampaignFormContext);

  const brandLogo = useFieldArray({
    control: guidelineRuleFormMethods.control,
    name: 'BRAND_LOGO',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    mutate: uploadGuideImageMutate,
    isPending: isUploadingBrandLogoImage,
  } = useUploadGuidelineImage();

  const onClickUploadBrandLogoImageButton = () => {
    if (!isUploadingBrandLogoImage) {
      fileInputRef.current.value = '';
      fileInputRef.current?.click();
    }
  };

  const onChangeBrandLogoImage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const image = event.target.files?.[0];

    if (image) {
      uploadGuideImageMutate(image, {
        onSuccess: (data) => {
          brandLogo.append({
            value: JSON.stringify({
              url: data.data.url,
              description: '',
            }),
          });
        },
        onError: (error: Error) => {
          modal.showModal({
            text: error?.message,
          });
        },
      });
    }
  };

  const onDeleteBrandLogo = (index: number) => {
    modal.showModal({
      text: languageTexts.create_delete_image_confirm,
      confirm: () => {
        brandLogo.remove(index);
      },
    });
  };

  return (
    <CampaignFormField
      as="div"
      wrapperClassName="!mt-[28px]"
      label={
        <>
          <span className="text-[17px]">
            {languageTexts.create_reels_form_logo_guideline}
          </span>
          <span className="px-[4px] py-[2px] text-white bg-black text-[12px] font-bold select-none leading-none">
            {languageTexts.recommendation}
          </span>
        </>
      }
      labelClassName="flex flex-row items-center gap-[6px]"
      descriptionClassName="text-sm text-[#FA0000]"
      description={languageTexts.create_reels_form_logo_guideline_desc}
    >
      {/** 로고 이미지 영역 */}
      <div className="flex flex-col gap-[10px]">
        {brandLogo.fields.map((field, index) => {
          const { url } = JSON.parse(field.value as string);

          return (
            <div key={`brand-logo-${index}`} className="relative flex flex-col">
              <div className="relative flex w-full max-h-[200px] border border-[#E8E8E8] bg-[#888888] p-[40px]">
                <img className="object-contain" src={url} />

                {/* 로고 삭제 버튼 */}
                <div
                  className="absolute top-0 right-0 cursor-pointer bg-black"
                  onClick={() => onDeleteBrandLogo(index)}
                >
                  <img className="p-1" src={'/assets/icons/close-white.svg'} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 로고 업로드 버튼 */}
      {brandLogo.fields.length < 2 && (
        <button
          type="button"
          className="flex justify-center items-center cursor-pointer mx-auto py-3 w-full border-[1px] border-[#E8E8E8]"
          onClick={onClickUploadBrandLogoImageButton}
        >
          {languageTexts.create_reels_form_logo_guideline_upload}
          <input
            type="file"
            className="hidden"
            ref={fileInputRef}
            onChange={onChangeBrandLogoImage}
          />
        </button>
      )}
    </CampaignFormField>
  );
};

export default memo(BrandLogoField);
